
import { Helmet } from "react-helmet";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Save, AlertCircle, Upload, Edit } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface HomeContent {
  hero: {
    heading: string;
    subheading: string;
  };
  features: Array<{
    title: string;
    description: string;
  }>;
}

interface AboutContent {
  mission: string;
  vision: string;
}

interface ServicesContent {
  heading: string;
  description: string;
  services: Array<{
    name: string;
    description: string;
  }>;
}

// Helper function to save content to localStorage
const saveContent = (key: string, content: any) => {
  localStorage.setItem(`edgefi_content_${key}`, JSON.stringify(content));
};

// Helper function to load content from localStorage
const loadContent = (key: string, defaultContent: any): any => {
  const savedContent = localStorage.getItem(`edgefi_content_${key}`);
  if (savedContent) {
    return JSON.parse(savedContent);
  }
  return defaultContent;
};

const Content = () => {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("home");
  const [isDirty, setIsDirty] = useState(false);
  
  // Content states for different pages
  const [homeContent, setHomeContent] = useState<HomeContent>({
    hero: {
      heading: "Focus on Your Business, Not Your IT Challenges",
      subheading: "Transform your technology from a constant concern into a strategic advantage. Get the reliable IT support your growing business deserves without the complexity."
    },
    features: [
      { title: "Proactive Security", description: "Prevent issues before they affect your business" },
      { title: "24/7 Support", description: "Expert help whenever you need it" },
      { title: "Strategic Planning", description: "Align technology with business goals" }
    ]
  });
  
  const [aboutContent, setAboutContent] = useState<AboutContent>({
    mission: "Cybersecurity designed to evolve with the times.",
    vision: "We are here to help our customers build security operations that evolve with the times and are ahead of the chaos."
  });
  
  const [servicesContent, setServicesContent] = useState<ServicesContent>({
    heading: "Our Services",
    description: "Comprehensive IT and cybersecurity solutions designed for your business needs",
    services: [
      { name: "Managed IT Services", description: "Ongoing IT support and management" },
      { name: "Cybersecurity", description: "Protect your business from threats" },
      { name: "Cloud Solutions", description: "Scalable cloud infrastructure" }
    ]
  });

  // Load saved content on component mount
  useEffect(() => {
    setHomeContent(loadContent('home', homeContent));
    setAboutContent(loadContent('about', aboutContent));
    setServicesContent(loadContent('services', servicesContent));
  }, []);

  // Track changes
  useEffect(() => {
    setIsDirty(true);
  }, [homeContent, aboutContent, servicesContent]);

  const handleSave = () => {
    // Save all content to localStorage
    saveContent('home', homeContent);
    saveContent('about', aboutContent);
    saveContent('services', servicesContent);
    
    setIsDirty(false);
    
    toast({
      title: "Content saved",
      description: "Your changes have been saved successfully.",
    });
  };

  // Update home content hero section
  const updateHomeHero = (field: keyof HomeContent['hero'], value: string) => {
    setHomeContent(prev => ({
      ...prev,
      hero: {
        ...prev.hero,
        [field]: value
      }
    }));
  };

  // Update home content features
  const updateHomeFeature = (index: number, field: keyof HomeContent['features'][0], value: string) => {
    setHomeContent(prev => {
      const newFeatures = [...prev.features];
      newFeatures[index] = {
        ...newFeatures[index],
        [field]: value
      };
      return {
        ...prev,
        features: newFeatures
      };
    });
  };

  // Add a new feature
  const addHomeFeature = () => {
    setHomeContent(prev => ({
      ...prev,
      features: [...prev.features, { title: "", description: "" }]
    }));
  };

  // Remove a feature
  const removeHomeFeature = (index: number) => {
    setHomeContent(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }));
  };

  // Update about content
  const updateAboutContent = (field: keyof AboutContent, value: string) => {
    setAboutContent(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Update services content
  const updateServicesContent = (field: 'heading' | 'description', value: string) => {
    setServicesContent(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Update a service
  const updateService = (index: number, field: keyof ServicesContent['services'][0], value: string) => {
    setServicesContent(prev => {
      const newServices = [...prev.services];
      newServices[index] = {
        ...newServices[index],
        [field]: value
      };
      return {
        ...prev,
        services: newServices
      };
    });
  };

  // Add a new service
  const addService = () => {
    setServicesContent(prev => ({
      ...prev,
      services: [...prev.services, { name: "", description: "" }]
    }));
  };

  // Remove a service
  const removeService = (index: number) => {
    setServicesContent(prev => ({
      ...prev,
      services: prev.services.filter((_, i) => i !== index)
    }));
  };

  return (
    <div className="space-y-6">
      <Helmet>
        <title>Manage Content | edgefi Admin</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Content Management</h1>
          <p className="text-gray-500">Edit website content from one central location</p>
        </div>
        <Button 
          onClick={handleSave} 
          className="flex items-center gap-2 bg-edgefi-purple hover:bg-edgefi-purple/90"
          disabled={!isDirty}
        >
          <Save size={16} />
          Save Changes
        </Button>
      </div>

      <Alert className="bg-blue-50 border-blue-200">
        <AlertCircle className="h-4 w-4 text-blue-600" />
        <AlertDescription className="text-blue-800">
          Changes made here will be stored locally in your browser for this demo. In a production environment, these would be saved to a database.
        </AlertDescription>
      </Alert>

      <Tabs defaultValue="home" value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="w-full justify-start border-b rounded-none p-0 h-auto">
          <TabsTrigger
            value="home"
            className={`rounded-none border-b-2 border-transparent px-4 py-2 ${
              activeTab === "home" ? "border-edgefi-purple" : ""
            }`}
          >
            Home Page
          </TabsTrigger>
          <TabsTrigger
            value="about"
            className={`rounded-none border-b-2 border-transparent px-4 py-2 ${
              activeTab === "about" ? "border-edgefi-purple" : ""
            }`}
          >
            About Page
          </TabsTrigger>
          <TabsTrigger
            value="services"
            className={`rounded-none border-b-2 border-transparent px-4 py-2 ${
              activeTab === "services" ? "border-edgefi-purple" : ""
            }`}
          >
            Services
          </TabsTrigger>
        </TabsList>

        <TabsContent value="home" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Hero Section</CardTitle>
              <CardDescription>Edit the main hero section on the homepage</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Heading</label>
                <textarea
                  className="w-full min-h-[100px] p-2 border rounded-md"
                  value={homeContent.hero.heading}
                  onChange={(e) => updateHomeHero('heading', e.target.value)}
                />
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium">Subheading</label>
                <textarea
                  className="w-full min-h-[100px] p-2 border rounded-md"
                  value={homeContent.hero.subheading}
                  onChange={(e) => updateHomeHero('subheading', e.target.value)}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Features Section</CardTitle>
                <CardDescription>Edit the feature cards on the homepage</CardDescription>
              </div>
              <Button size="sm" onClick={addHomeFeature} className="flex items-center gap-1">
                <Upload size={14} />
                Add Feature
              </Button>
            </CardHeader>
            <CardContent>
              {homeContent.features.map((feature, index) => (
                <div key={index} className="border rounded-md p-4 mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-medium">Feature {index + 1}</h3>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => removeHomeFeature(index)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    >
                      Remove
                    </Button>
                  </div>
                  
                  <div className="space-y-2 mb-3">
                    <label className="text-sm font-medium text-gray-500">Title</label>
                    <input
                      type="text"
                      className="w-full p-2 border rounded-md"
                      value={feature.title}
                      onChange={(e) => updateHomeFeature(index, 'title', e.target.value)}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-500">Description</label>
                    <input
                      type="text"
                      className="w-full p-2 border rounded-md"
                      value={feature.description}
                      onChange={(e) => updateHomeFeature(index, 'description', e.target.value)}
                    />
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="about" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>About Page Content</CardTitle>
              <CardDescription>Edit the content for the About page</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Mission Statement</label>
                <textarea
                  className="w-full min-h-[100px] p-2 border rounded-md"
                  value={aboutContent.mission}
                  onChange={(e) => updateAboutContent('mission', e.target.value)}
                />
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium">Vision</label>
                <textarea
                  className="w-full min-h-[100px] p-2 border rounded-md"
                  value={aboutContent.vision}
                  onChange={(e) => updateAboutContent('vision', e.target.value)}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="services" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Services Page Content</CardTitle>
              <CardDescription>Edit the content for the Services pages</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Page Heading</label>
                <input
                  type="text"
                  className="w-full p-2 border rounded-md"
                  value={servicesContent.heading}
                  onChange={(e) => updateServicesContent('heading', e.target.value)}
                />
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium">Page Description</label>
                <textarea
                  className="w-full min-h-[100px] p-2 border rounded-md"
                  value={servicesContent.description}
                  onChange={(e) => updateServicesContent('description', e.target.value)}
                />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Services List</CardTitle>
                <CardDescription>Manage the services offered</CardDescription>
              </div>
              <Button size="sm" onClick={addService} className="flex items-center gap-1">
                <Upload size={14} />
                Add Service
              </Button>
            </CardHeader>
            <CardContent>
              {servicesContent.services.map((service, index) => (
                <div key={index} className="border rounded-md p-4 mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-medium">Service {index + 1}</h3>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => removeService(index)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    >
                      Remove
                    </Button>
                  </div>
                  
                  <div className="space-y-2 mb-3">
                    <label className="text-sm font-medium text-gray-500">Service Name</label>
                    <input
                      type="text"
                      className="w-full p-2 border rounded-md"
                      value={service.name}
                      onChange={(e) => updateService(index, 'name', e.target.value)}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-500">Service Description</label>
                    <textarea
                      className="w-full min-h-[80px] p-2 border rounded-md"
                      value={service.description}
                      onChange={(e) => updateService(index, 'description', e.target.value)}
                    />
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Content;
